import { ToastIcon } from '@/components/bs-icons/toast'
import { cname } from '@/components/bs-ui/utils'
import { CaretDownIcon } from '@radix-ui/react-icons'
import { useMemo, useState } from 'react'
import { CitationsType } from './types'

/**
 * 运行日志组件
 *
 * 这是聊天系统中用于显示AI助手运行日志的组件，包括插件调用、知识库搜索等操作的执行状态。
 * 支持折叠展开功能，显示详细的执行信息。
 * @param data - 运行日志数据数组，包含插件调用和知识库搜索等信息
 */
export default function RunLog({ data = [] }: { data: CitationsType[] }) {
  // 折叠展开状态控制
  const [open, setOpen] = useState(false)

  /**
   * 处理运行日志数据，生成标题和详细信息
   * 根据日志类型（插件或知识库）生成相应的显示文本
   */
  const [title, detail] = useMemo(() => {
    if (!data.length) {
      return ['', '']
    }

    // 溯源信息中最多包含一种类型
    const type = data[0].type
    const startInfo = type === 'knowledge' ? '已搜索' : '已使用'

    let title = ''
    if (type === 'plugin') {
      // 插件类型：显示已使用的插件名称
      title = startInfo + data.map(item => item.title).filter(item => item)
    } else if (type === 'knowledge') {
      // 知识库类型：显示已搜索的知识库名称
      title = startInfo + data.map(item => item.knowledge_name).filter(item => item)
    }

    let detail = ''
    try {
      // 将详细信息转换为JSON字符串，多个项目用换行符分隔
      detail = data.map(item => (item.detail?.length ? JSON.stringify(item.detail) : '')).join('\n')
    } catch (error) {
      console.error('data detail解析异常', error)
      detail = '详细信息解析异常'
    }
    return [title, detail]
  }, [data])

  return (
    <>
      {data.length ? (
        <div className="py-1">
          <div className="rounded-sm border">
            {/* 日志头部：标题和展开/收起按钮 */}
            <div className="flex cursor-pointer items-center justify-between px-4 py-2" onClick={() => setOpen(!open)}>
              <div className="flex items-center gap-2 text-sm font-bold">
                {/* 成功状态图标 */}
                <ToastIcon type="success" />
                <span>{title}</span>
              </div>
              {/* 展开/收起箭头图标 */}
              {!!detail && <CaretDownIcon className={open ? 'rotate-180' : ''} />}
            </div>

            {/* 详细信息区域：可折叠展开 */}
            <div
              className={cname(
                'overflow-hidden bg-gray-100 px-4 py-2 text-sm text-gray-500 ',
                !!detail && open ? 'h-auto' : 'h-0 p-0'
              )}
            >
              <p>{detail}</p>
            </div>
          </div>
        </div>
      ) : (
        <></>
      )}
    </>
  )
}
